import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import BucketJourney from "@/services/bucket/bucket-journey";
import type { PaginatedBucketJourneyResponse } from "@/dtos/BucketJourney.dto";

export interface BucketJourniesParams {
  contactId: string;
  organizationId: string;
  page?: number;
  limit?: number;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useBucketJournies(
  params: Ref<BucketJourniesParams | null> | BucketJourniesParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["bucketJournies", "disabled"];
    return ["bucketJournies", p.contactId, p.organizationId, p.page, p.limit];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.contactId && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const result = await BucketJourney.getBucketJournies({
        contactId: p.contactId,
        organizationId: p.organizationId,
        page: p.page || 1,
        limit: p.limit || 10,
      });

      if (result.data.status) {
        return result.data.data as PaginatedBucketJourneyResponse;
      }

      throw new Error(result.data.msg || "Failed to load bucket journies");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
